import type {
  ImpactCategory,
  MatrixEntry,
  RiskScoringLevel,
} from '@risksmart-app/atomic-ui';

import type { ScoringSettingsData } from './useRiskScoringSettingsStore';

export type ChangeStatus = 'none' | 'cosmetic' | 'structural';

export const analyzeChanges = (
  initial: ScoringSettingsData,
  current: ScoringSettingsData
): ChangeStatus => {
  if (hasStructuralChanges(initial, current)) {
    return 'structural';
  }

  if (hasCosmeticChanges(initial, current)) {
    return 'cosmetic';
  }

  return 'none';
};

/** Has structural changes when any of the core scoring settings have been modified */
const hasStructuralChanges = (
  initial: ScoringSettingsData,
  current: ScoringSettingsData
): boolean => {
  // Likelihood rating values (added, removed, or modified)
  const initialLikelihoodValues = initial.likelihoodLevels
    .map((l) => l.value)
    .sort((a, b) => a - b);
  const currentLikelihoodValues = current.likelihoodLevels
    .map((l) => l.value)
    .sort((a, b) => a - b);
  if (!arraysEqual(initialLikelihoodValues, currentLikelihoodValues)) {
    return true;
  }

  // Impact rating values (added, removed, or modified)
  const initialImpactValues = initial.impactLevels
    .map((l) => l.value)
    .sort((a, b) => a - b);
  const currentImpactValues = current.impactLevels
    .map((l) => l.value)
    .sort((a, b) => a - b);
  if (!arraysEqual(initialImpactValues, currentImpactValues)) {
    return true;
  }

  // Impact categories count
  if (initial.impactCategories.length !== current.impactCategories.length) {
    return true;
  }

  // Aggregation method
  if (initial.impactAggregation !== current.impactAggregation) {
    return true;
  }

  // Matrix entry count
  if (initial.matrix.length !== current.matrix.length) {
    return true;
  }

  // Matrix entry values
  const sortedInitialMatrix = [...initial.matrix].sort(
    (a, b) => a.value - b.value
  );
  const sortedCurrentMatrix = [...current.matrix].sort(
    (a, b) => a.value - b.value
  );

  for (let i = 0; i < sortedInitialMatrix.length; i++) {
    if (sortedInitialMatrix[i]?.value !== sortedCurrentMatrix[i]?.value) {
      return true;
    }
  }

  // Matrix likelihood/impact pairs
  for (let i = 0; i < sortedInitialMatrix.length; i++) {
    const initialPairs = sortedInitialMatrix[i]?.likelihoodImpact || [];
    const currentPairs = sortedCurrentMatrix[i]?.likelihoodImpact || [];
    if (!areLikelihoodImpactPairsEqual(initialPairs, currentPairs)) {
      return true;
    }
  }

  return false;
};

/** Has cosmetic changes when any of the non-core scoring settings (i.e., titles, descriptions, colors) have been modified */
const hasCosmeticChanges = (
  initial: ScoringSettingsData,
  current: ScoringSettingsData
): boolean =>
  !areLevelsEqualCosmetic(initial.likelihoodLevels, current.likelihoodLevels) ||
  !areLevelsEqualCosmetic(initial.impactLevels, current.impactLevels) ||
  !areCategoriesEqualCosmetic(
    initial.impactCategories,
    current.impactCategories
  ) ||
  !areMatrixEntriesEqualCosmetic(initial.matrix, current.matrix);

const areLevelsEqualCosmetic = (
  a: RiskScoringLevel[],
  b: RiskScoringLevel[]
): boolean => {
  if (a.length !== b.length) {
    return false;
  }

  return a.every((level, i) => {
    const other = b[i];

    return (
      level.title === other.title &&
      level.description === other.description &&
      level.color === other.color
    );
  });
};

const areCategoriesEqualCosmetic = (
  a: ImpactCategory[],
  b: ImpactCategory[]
): boolean => {
  if (a.length !== b.length) {
    return false;
  }

  return a.every((category, i) => {
    const other = b[i];

    return category.name === other.name && category.color === other.color;
  });
};

const areMatrixEntriesEqualCosmetic = (
  a: MatrixEntry[],
  b: MatrixEntry[]
): boolean => {
  if (a.length !== b.length) {
    return false;
  }

  return a.every((entry, i) => {
    const other = b[i];

    return entry.title === other.title && entry.color === other.color;
  });
};

const arraysEqual = (a: number[], b: number[]): boolean =>
  a.length === b.length && a.every((v, i) => v === b[i]);

const areLikelihoodImpactPairsEqual = (
  a: { likelihood: number; impact: number }[],
  b: { likelihood: number; impact: number }[]
): boolean => {
  const sortA = [...a].sort((x, y) =>
    x.likelihood !== y.likelihood
      ? x.likelihood - y.likelihood
      : x.impact - y.impact
  );
  const sortB = [...b].sort((x, y) =>
    x.likelihood !== y.likelihood
      ? x.likelihood - y.likelihood
      : x.impact - y.impact
  );

  return (
    sortA.length === sortB.length &&
    sortA.every(
      (pair, i) =>
        pair.likelihood === sortB[i]?.likelihood &&
        pair.impact === sortB[i]?.impact
    )
  );
};
